import { Suspense, useDeferredValue } from 'react'
import { createFileRoute, useMatch } from '@tanstack/react-router'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { ChartCard, TimeChart } from './-components/admin/metrics-charts'
import { formatCompact, formatCount } from './-components/admin/metrics-format'
import {
  CHART_ROW,
  ChartRowSkeleton,
  PlatformPending,
  TILE_GRID,
  TilesSkeleton,
} from './-components/admin/metrics-pending'
import { AudienceChart, MetricsSection, SMALL_PLOT, useZoom } from './-components/admin/metrics-panels'

// What exists, counted in Postgres, beside who signed up, signed in and came by over the window.

export const Route = createFileRoute('/_app/admin/metrics/platform')({
  // The counts are of right now, so they live here and not on the window's layout below.
  context: () => ({
    metricsSummaryQueryOptions: trpc.admin.metricsSummary.queryOptions(),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.metricsSummaryQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: PlatformPending,
})

function useWindowContext() {
  return useMatch({ from: '/_app/admin/metrics/platform/_window' }).context
}

function RouteComponent() {
  return (
    <div className={SETTINGS_STACK}>
      <MetricsSection copy="platform">
        <div className="flex flex-col gap-3">
          <Suspense fallback={<TilesSkeleton count={8} />}>
            <Counts />
          </Suspense>
          <Suspense fallback={<ChartRowSkeleton count={3} />}>
            <Charts />
          </Suspense>
        </div>
      </MetricsSection>
    </div>
  )
}

function Counts() {
  const { data: s } = useSuspenseQuery(Route.useRouteContext().metricsSummaryQueryOptions)
  return (
    <div className={TILE_GRID}>
      <CountTile label="Companies" value={s.companies} hint={`${formatCount(s.companiesLast30d)} new in 30 days`} />
      <CountTile
        label="People assessed"
        value={s.assessed.competences}
        hint={`competences · ${formatCount(s.assessed.values)} values · ${formatCount(s.assessed.personality)} personality`}
      />
      <CountTile label="Offers" value={s.offers} hint="recruiting, not deleted" />
      <CountTile label="Positions" value={s.positions} hint="internal roles, not deleted" />
      <CountTile label="Employees" value={s.employees} hint="company memberships" />
      <CountTile label="Teams" value={s.teams} hint="not deleted" />
      <CountTile label="Assistant threads" value={s.assistantThreadsLast30d} hint="started in 30 days" />
    </div>
  )
}

function CountTile(props: { label: string; value: number; hint: string }) {
  return (
    <div className="bg-card flex min-h-[92px] flex-col gap-0.5 rounded-lg border p-3">
      <span className="text-muted-foreground truncate text-xs">{props.label}</span>
      <span className="text-xl font-semibold tracking-tight tabular-nums sm:text-2xl">{formatCount(props.value)}</span>
      <span className="text-muted-foreground truncate text-xs" title={props.hint}>
        {props.hint}
      </span>
    </div>
  )
}

function Charts() {
  const context = useWindowContext()
  const growthOptions = useDeferredValue(context.metricsGrowthQueryOptions)
  const trafficOptions = useDeferredValue(context.metricsTrafficQueryOptions)
  const { data } = useSuspenseQuery(growthOptions)
  const { data: traffic } = useSuspenseQuery(trafficOptions)
  const stale =
    growthOptions !== context.metricsGrowthQueryOptions || trafficOptions !== context.metricsTrafficQueryOptions
  const zoom = useZoom()
  const grid = { bucketMs: data.bucketMs, spanMs: data.spanMs }
  return (
    <div className={cn(CHART_ROW, 'transition-opacity', stale && 'opacity-60')}>
      <ChartCard title="Companies and jobs created" description="Company accounts, offers and positions (Postgres).">
        <TimeChart
          grid={grid}
          points={data.growth}
          format={formatCompact}
          onZoom={zoom}
          height={SMALL_PLOT}
          series={[
            { key: 'companies', label: 'Companies', color: 'var(--series-1)' },
            { key: 'offers', label: 'Offers', color: 'var(--series-2)' },
            { key: 'positions', label: 'Positions', color: 'var(--series-3)' },
          ]}
        />
      </ChartCard>
      <ChartCard
        title="Sign-ups and logins"
        description="Accounts created, logins, and failed password logins."
        offline={data.clickhouse !== 'ok'}
      >
        <TimeChart
          grid={grid}
          points={data.auth}
          format={formatCompact}
          onZoom={zoom}
          height={SMALL_PLOT}
          series={[
            { key: 'signups', label: 'Sign-ups', color: 'var(--series-1)' },
            { key: 'logins', label: 'Logins', color: 'var(--series-3)' },
            { key: 'failedLogins', label: 'Failed logins', color: 'var(--series-error)' },
          ]}
        />
      </ChartCard>
      <AudienceChart data={traffic} height={SMALL_PLOT} />
    </div>
  )
}
